import { sessionService } from "../state/ducks/session/sessionService";

const DEDUPE_WINDOW_MS = 5000;
const RATE_WINDOW_MS = 10000;
const MAX_EVENTS_PER_WINDOW = 20;
const NON_CRITICAL_SAMPLE_RATE = 0.05;

let rateWindowStart = Date.now();
let sentInRateWindow = 0;
const recentEventTimestamps = new Map();

const CRITICAL_EVENT_NAMES = [
  "ajax error",
  "window error",
  "unhandled rejection",
  "react error boundary",
  "sessionstorage parse error",
  "sessionStorage parse error",
];

function isCriticalEvent(data) {
  const eventName = String(data?.eventName || "").trim();
  const normalized = eventName.toLowerCase();

  if (!normalized) return false;

  return CRITICAL_EVENT_NAMES.some(name => name.toLowerCase() === normalized)
    || normalized.includes("error")
    || normalized.includes("rejection")
    || normalized.includes("failed");
}

function shouldSampleEvent(data) {
  if (process.env.NODE_ENV !== "production") {
    return true;
  }

  if (isCriticalEvent(data)) {
    return true;
  }

  return Math.random() < NON_CRITICAL_SAMPLE_RATE;
}

function makeEventKey(data) {
  const eventName = data?.eventName || "unknown";
  const trackId = data?.trackId || "unknown";
  const details = data?.details;

  try {
    // Keep keys small and stable while still distinguishing repeated errors.
    const serializedDetails = JSON.stringify(details);
    return `${trackId}|${eventName}|${serializedDetails}`;
  } catch {
    return `${trackId}|${eventName}|unserializable-details`;
  }
}

function shouldDropEvent(data) {
  const now = Date.now();

  if (now - rateWindowStart >= RATE_WINDOW_MS) {
    rateWindowStart = now;
    sentInRateWindow = 0;
  }

  if (sentInRateWindow >= MAX_EVENTS_PER_WINDOW) {
    return true;
  }

  for (const [key, timestamp] of recentEventTimestamps.entries()) {
    if (now - timestamp >= DEDUPE_WINDOW_MS) {
      recentEventTimestamps.delete(key);
    }
  }

  const eventKey = makeEventKey(data);
  const previousTimestamp = recentEventTimestamps.get(eventKey);
  if (previousTimestamp && now - previousTimestamp < DEDUPE_WINDOW_MS) {
    return true;
  }

  recentEventTimestamps.set(eventKey, now);
  sentInRateWindow += 1;
  return false;
}

export function logEvent(data) {
  if(!data) return;
  if (!shouldSampleEvent(data)) return;
  if (shouldDropEvent(data)) return;
  
  try {
    setTimeout(() => {
      // Fire-and-forget log delivery: swallow failures so a failed
      // /api/session/logging call cannot emit unhandledrejection and recurse.
      Promise.resolve(sessionService.logging(data)).catch(() => null);
    })
  }
  catch(err) {
    console.log('error: ', err)
  }
}